import {ObjUtil, Util} from 'yougile-util'
import {DataObjectId} from './data-object'
import {JSONLike} from './diff'
import {Entity, EntityJSON} from './entity'
import {
  StickerIcon,
  stickerIcons,
  StickerType,
  stickerTypes
} from './sticker-constants'
import {
  newToOldStickerColors,
  OldStickerColors,
  oldToNewStickerColors,
  StickerColors
} from './sticker-data'

export interface TagData {
  by?: DataObjectId
}

export interface StickerStates {
  index: {[id: string]: JSONLike}
  order: string[]
}

export interface TagJSON extends EntityJSON {
  data?: TagData
  // available sticker states for
  // stickers with user defined list of states
  states?: StickerStates
}

export class Tag extends Entity {
  data: TagData
  states: StickerStates

  constructor(companyId: DataObjectId, title: string) {
    super(companyId, title)
    this.dataType = 'Tag'
    this.data = {}
    this.states = {
      index: {},
      order: []
    }
    this.isLazy = false
  }

  override toJSON(): TagJSON {
    const res = super.toJSON() as TagJSON
    if (!ObjUtil.isEmpty(this.data)) {
      res.data = Util.clone(this.data)
    }
    if (!ObjUtil.isEmpty(this.states.index)) {
      res.states = Util.clone(transformStatesColorsToKeys(this.states, true))
    }
    return res
  }

  override fromJSON(
    companyId: DataObjectId,
    data: TagJSON,
    transformStatesColors = true
  ): Tag {
    super.fromJSON(companyId, data)
    this.data = data.data ? Util.clone(data.data) : {}
    const defaultState: StickerStates = {
      index: {},
      order: []
    }

    const tagIcon = data?.properties?.stickerIcon as StickerIcon
    const tagType = data?.properties?.stickerType as StickerType
    if (
      tagType &&
      tagIcon &&
      stickerTypes.includes(tagType) &&
      !stickerIcons.includes(tagIcon)
    ) {
      this.properties.stickerIcon = ''
    }

    if (data.states?.index) {
      this.states =
        Util.clone(
          transformStatesColors
            ? transformStatesColorsToKeys(data.states, false)
            : data.states
        ) || defaultState
    } else {
      this.states = defaultState
    }
    return this
  }
}

const transformStatesColorsToKeys = (
  states: StickerStates,
  reversed = false
) => {
  const stickerStatesIds = states?.order ?? Object.keys(states?.index)

  if (!stickerStatesIds) {
    return undefined
  }

  return stickerStatesIds.reduce(
    (acc, stateIndex) => {
      if (stateIndex === undefined) {
        return acc
      }

      const currentIndex = states?.index?.[stateIndex] || {}

      if (!currentIndex?.color) {
        return acc
      }

      const stickerColor = reversed
        ? (newToOldStickerColors[
            currentIndex.color as StickerColors
          ] as OldStickerColors)
        : (oldToNewStickerColors[
            currentIndex.color as OldStickerColors
          ] as StickerColors)

      if (stickerColor) {
        return {
          ...acc,
          index: {
            ...acc.index,
            [stateIndex]: {
              ...currentIndex,
              color: stickerColor
            }
          }
        }
      }

      return acc
    },
    {index: {...states.index}, order: states.order}
  )
}
